import { createFileRoute, notFound, useLocation } from "@tanstack/react-router";
import { z } from "zod";

import { resolveSessionIdentity } from "~/components/nova/personas";
import { NovaSession } from "~/components/nova/session/nova-session";
import { ButtonRouter } from "~/components/ui/button";
import { PageTitle } from "~/components/ui/page-title";
import { getNovaConversation } from "~/lib/functions/nova";
import { isAssetKey, type AssetKey } from "~/lib/shared/nova/assets/catalog";
import { planStepNameSchema } from "~/lib/shared/nova/plan-steps";
import { isSkillName, type SkillName } from "~/lib/shared/nova/skill-metadata";

/**
 * Mutually exclusive auto-send deep-links, all cleared by the chat after one
 * send:
 *  - `revisitStep` re-enters a specific COMPLETED Plan step (the path's revisit
 *    click sets it; the chat sends that step's revisit prompt).
 *  - `startStep` FIRST-enters a specific not-started but enterable Plan step
 *    (the path's Start on an enterable step sets it; the chat sends that step's
 *    kickoff prompt — the coach has never done it).
 *  - `kickoff` opens a fresh session by auto-sending its opening message (a room
 *    or door start CTA sets it; the chat resolves the canned kickoff copy from
 *    the agent/step). Boolean, never free text — the message is looked up
 *    client-side, so the URL can't inject a message.
 *  - `skill` opens a session a deterministic asset start (`startAsset`) already
 *    seeded with that skill; the chat sends that asset's catalog starter
 *    prompt. The param names the skill, never the text — same rule as above.
 * All optional — absent for an idle direct visit, which keeps the welcome.
 *
 * `asset` is not an auto-send signal and survives the clearing: it names the
 * asset this conversation was opened for, so the session rail shows that one.
 * An unknown key reads as absent rather than failing the route.
 */
const conversationSearchSchema = z.object({
  revisitStep: planStepNameSchema.optional(),
  startStep: planStepNameSchema.optional(),
  kickoff: z.boolean().optional(),
  skill: z
    .custom<SkillName>(
      (value) => typeof value === "string" && isSkillName(value),
      { message: "Unknown skill" },
    )
    .optional(),
  asset: z
    .custom<AssetKey>((value) => typeof value === "string" && isAssetKey(value))
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute(
  "/_user/my-coaching/business/nova/$conversationId",
)({
  staticData: { title: "Session" },
  validateSearch: conversationSearchSchema,
  loader: async ({ params }) => {
    // A path that is not a session id, such as a bookmark to a removed Nova
    // page, is a miss too; the server function would reject it as invalid.
    if (!z.string().uuid().safeParse(params.conversationId).success) {
      throw notFound();
    }
    const conversation = await getNovaConversation({
      data: { conversationId: params.conversationId },
    });
    // Missing / not-yours session: render the not-found UI rather than an
    // empty session shell. `getNovaConversation` returns null here, so this
    // is the expected miss and never reaches Sentry (notFound is control flow).
    if (!conversation) throw notFound();
    if (conversation.title) return { title: conversation.title };
    const identity = resolveSessionIdentity(conversation.agentType);
    return {
      title: identity.kind === "persona" ? identity.phase.label : "Session",
    };
  },
  notFoundComponent: SessionNotFound,
  component: NovaConversation,
});

function SessionNotFound() {
  return (
    <div className="mx-auto max-w-xl py-16 text-center">
      <PageTitle>Session not found</PageTitle>
      <p className="text-text-secondary mt-2">
        This Nova session doesn't exist anymore or belongs to another account.
      </p>
      <div className="mt-6">
        <ButtonRouter to="/my-coaching/business/nova" variant="primary">
          Back to Nova
        </ButtonRouter>
      </div>
    </div>
  );
}

function NovaConversation() {
  const { conversationId } = Route.useParams();
  const { revisitStep, startStep, kickoff, skill, asset } = Route.useSearch();
  const userId = Route.useRouteContext({
    select: (context) => context.user.clerkUserId,
  });
  // Read, not trusted: `resolveAutoSendMessage` validates it before sending.
  const note = useLocation({
    select: (location) => location.state.assetNote ?? null,
  });
  return (
    <NovaSession
      conversationId={conversationId}
      userId={userId}
      revisitStep={revisitStep ?? null}
      startStep={startStep ?? null}
      kickoff={kickoff ?? false}
      skill={skill ?? null}
      asset={asset ?? null}
      note={note}
    />
  );
}
